import { useQuery } from '@apollo/client';
import { EFilterType, TFilter, TFilterEvent } from 'Components/Filter/filter.types';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { GET_COMPONENTS_LIST } from 'graphql/secure/queries';
import { useLogEvent } from 'hooks/useLogEvent';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { IGraphQlConnection } from 'types/core.types';
import { ESortOrder } from 'types/pagination.types';
import { TRouteParamsWithId } from 'types/router.types';
import { useDebouncedCallback } from 'use-debounce';
import {
  ArrayParam,
  createEnumParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

const usePartnerComponentItems = ({ defaultSearchTerm }: { defaultSearchTerm?: string } = {}) => {
  const { id: companyId } = useParams<TRouteParamsWithId>();
  const { logEvent } = useLogEvent();
  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, defaultSearchTerm || ''),
      rawMaterialIds: withDefault(ArrayParam, undefined),
      orderByMethod: withDefault(createEnumParam([ESortOrder.ASC, ESortOrder.DESC]), undefined),
      orderByField: withDefault(StringParam, undefined),
    },
    { removeDefaultsFromUrl: true }
  );
  const { rawMaterialIds, orderByMethod, orderByField, searchTerm } = query;

  const { data, error, loading, refetch } = useQuery<{
    components: IGraphQlConnection<IComponentItem>;
  }>(GET_COMPONENTS_LIST, {
    variables: {
      filters: {
        ownedById: companyId,
        ...(searchTerm ? { searchTerm } : {}),
        ...(rawMaterialIds ? { rawMaterialIds } : {}),
      },
      orderBy: { orderByMethod, orderByField },
    },
  });

  const filters: TFilter<EFilterType.RAW_MATERIALS>[] = useMemo(
    () => [
      {
        type: EFilterType.RAW_MATERIALS,
        value: rawMaterialIds
          ? (rawMaterialIds.filter((id): id is string => id !== null) as string[])
          : undefined,
        multiple: true,
        label: 'Raw materials',
        placeholder: 'All raw materials',
      },
    ],
    [rawMaterialIds]
  );

  const debouncedLogEvent = useDebouncedCallback(() => {
    logEvent('COMPONENT_SEARCHED', { searchTerm });
  }, 500);

  const handleSearchTermChange = (searchTerm: string) => {
    setQuery({ searchTerm });
    debouncedLogEvent.callback();
  };

  const components: IComponentItem[] = useMemo(() => {
    if (data?.components) {
      return removeGraphConnections(data.components);
    }
    return [];
  }, [data?.components]);

  const handleFilterReset = () => {
    setQuery(prev => ({
      ...prev,
      rawMaterialIds: undefined,
    }));
  };

  const handleFilterChange = (event: TFilterEvent) => {
    if (event.type === EFilterType.RAW_MATERIALS) {
      setQuery(prev => ({
        ...prev,
        rawMaterialIds: (event.value as string[]) || undefined,
      }));
    }
  };

  return {
    error,
    loading,
    components,
    filters,
    searchTerm,
    handleSearchTermChange,
    refetch,
    handleFilterReset,
    handleFilterChange,
  };
};

export default usePartnerComponentItems;
